/**
 * `stalled` and `timedOut` from one elapsed reading, shared by every poll on this page so
 * the two flags can never be computed from two different notions of "elapsed" or drift to
 * two different settle conditions. `timedOut` is stricter and later-firing than `stalled`
 * for any caller whose own `pollTimeoutMs` is greater than its `stallThresholdMs` — every
 * `timedOut` moment is also `stalled`, which is fine, since the caller decides which one
 * to show, not this function.
 *
 * `timedOut` additionally requires `!isFetching` — the ceiling must only fire on a
 * SETTLED, still-non-terminal answer. Without that gate, a tab refocused after the
 * watched pipeline completed while it was hidden would read `elapsedMs` off the stale
 * pre-fetch data for the one render where the fresh fetch is already in flight but has
 * not landed yet, and flash a timed-out state for that render — a confirmed failure shown
 * for a request that, moments later, turns out to have succeeded. `stalled` carries no
 * such gate: it only ever changes the progress copy, never which state is shown, so the
 * same one-render flash is harmless there.
 *
 * Each caller passes its own thresholds. `use-parse-import-queries.ts`'s two polls and
 * `use-booking-status.ts`'s poll derive `stallThresholdMs`/`pollTimeoutMs` from unrelated
 * recovery windows — the parser's and the attachment cell's own retry windows for one,
 * the position cell's booking crons for the other — that happen to land on the same
 * numbers. Sharing this function shares the mechanism, not the derivation; each hook
 * keeps its own named constants and its own comment on where they come from.
 */
export function deriveTimingFlags(
  elapsedMs: number | null,
  isActive: boolean,
  isFetching: boolean,
  stallThresholdMs: number,
  pollTimeoutMs: number,
): { stalled: boolean; timedOut: boolean } {
  const stalled = isActive && elapsedMs != null && elapsedMs > stallThresholdMs;
  const timedOut = isActive && !isFetching && elapsedMs != null && elapsedMs > pollTimeoutMs;
  return { stalled, timedOut };
}
